import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";

import { apiFetch, platformInfo } from "../../Helper/Api";
import { t } from "../../Helper/i18n";
import Header from "../../components/Header/Header";
import { Alert } from "../../components/UI/Feedback";
import { ThemeBar, StateChip, themeOf, shows, siteOf, BLOCK_NAMES, SUBJECT_NAMES } from "../Settings/SiteEditor";

import "./SiteContent.scss";

const properties = require("../../properties.json");

// What the home-page blocks of the site theme show (statistics, features, testimonials, questions, store links, and
// for the newer themes the steps, the honour board and the specialty section). The same limits as the server
// (HomeContent) so a form never produces something the server refuses. Used by every category: a school teacher
// reaches it from Settings, a coach from their own menu; the words come from each pack's content.* messages.
const LIMITS = { stats: 4, features: 6, testimonials: 6, faq: 8, steps: 4, honor: 8, subjectItems: 6 };
const SHAPES = {
  stats: { value: "", label: "" },
  features: { icon: "", title: "", text: "" },
  testimonials: { name: "", role: "", text: "" },
  faq: { q: "", a: "" },
  steps: { title: "", text: "" },
  honor: { name: "", score: "" },
  subjectItems: { title: "", sub: "" },
};
const TEXTS = ["heroCtaLabel", "ctaTitle", "ctaText", "subjectEyebrow", "subjectTitle", "subjectText"];

/** The branding fields this page sends back unchanged (the server replaces the whole block on save). */
const KEEP = ["siteTitle", "tagline", "heroEyebrow", "heroTitle", "heroText", "heroImageUrl", "featureTitle", "featureText",
  "featureImageUrl", "aboutJson", "downloadUrl", "faviconUrl"];

export function emptyContent() {
  const base = { stats: [], features: [], testimonials: [], faq: [], steps: [], honor: [], subjectItems: [], apps: { ios: "", android: "" } };
  TEXTS.forEach((k) => {
    base[k] = "";
  });
  return base;
}

/** The saved text -> what the form edits. */
export function readContent(homeJson) {
  const base = emptyContent();
  let raw = {};
  try {
    raw = homeJson ? JSON.parse(homeJson) : {};
  } catch (e) {
    raw = {};
  }
  Object.keys(SHAPES).forEach((k) => {
    base[k] = (Array.isArray(raw[k]) ? raw[k] : [])
      .filter((i) => i && typeof i === "object")
      .slice(0, LIMITS[k])
      .map((i) => ({ ...SHAPES[k], ...i }));
  });
  TEXTS.forEach((k) => {
    base[k] = typeof raw[k] === "string" ? raw[k] : "";
  });
  const apps = raw.apps && typeof raw.apps === "object" ? raw.apps : {};
  base.apps = { ios: typeof apps.ios === "string" ? apps.ios : "", android: typeof apps.android === "string" ? apps.android : "" };
  return base;
}

/** The form -> the text to save: empty rows and empty fields are left out. */
export function writeContent(form) {
  const out = {};
  TEXTS.forEach((k) => {
    if (form[k] && form[k].trim()) out[k] = form[k].trim();
  });
  Object.keys(SHAPES).forEach((k) => {
    const rows = (form[k] || [])
      .map((r) => Object.fromEntries(Object.entries(r).map(([f, v]) => [f, (v || "").trim()]).filter(([, v]) => v)))
      .filter((r) => Object.keys(r).length > 0)
      .slice(0, LIMITS[k]);
    if (rows.length) out[k] = rows;
  });
  const apps = {};
  ["ios", "android"].forEach((k) => {
    if (form.apps[k] && form.apps[k].trim()) apps[k] = form.apps[k].trim();
  });
  if (Object.keys(apps).length) out.apps = apps;
  return Object.keys(out).length ? JSON.stringify(out) : "";
}

/** One box with its name above it and, where there is a limit, how much of it is used. */
function Box({ label, value, max, area, wide, dir, placeholder, onChange }) {
  return (
    <label className={`se-f ${wide ? "is-wide" : ""}`}>
      <span>
        {label}
        {max ? <small dir="ltr">{(value || "").length}/{max}</small> : null}
      </span>
      {area ? (
        <InputTextarea rows={2} autoResize value={value} maxLength={max} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <InputText value={value} maxLength={max || 80} dir={dir} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );
}

function Rows({ name, form, setForm, fields }) {
  const rows = form[name];
  const update = (i, f, v) => setForm({ ...form, [name]: rows.map((r, idx) => (idx === i ? { ...r, [f]: v } : r)) });
  return (
    <div className="se-rows">
      {rows.map((r, i) => (
        <div className="se-row" key={i}>
          <span className="i">{i + 1}</span>
          <div className="se-row-fields">
            {fields.map(([f, label, opts]) => (
              <Box key={f} label={label} value={r[f]} max={(opts && opts.max) || 80} area={opts && opts.area} wide={opts && opts.wide}
                onChange={(v) => update(i, f, v)} />
            ))}
          </div>
          <button type="button" className="se-row-del" aria-label={t("common.delete")}
            onClick={() => setForm({ ...form, [name]: rows.filter((_, idx) => idx !== i) })}>
            <i className="pi pi-trash"></i>
          </button>
        </div>
      ))}
      {rows.length < LIMITS[name] ? (
        <button type="button" className="se-add" onClick={() => setForm({ ...form, [name]: [...rows, { ...SHAPES[name] }] })}>
          <i className="pi pi-plus"></i>
          {t("content.add")}
          <small>({rows.length} / {LIMITS[name]})</small>
        </button>
      ) : (
        <div className="se-full">{`وصلت للحد: ${LIMITS[name]}`}</div>
      )}
    </div>
  );
}

// Every section of the form, with the theme blocks that show it. `fallback` is what the site shows while the section
// is empty: "auto" its own words, "school" its own words in the newer themes only, otherwise nothing.
const SECTIONS = [
  { id: "sc-cta", key: "cta", title: "content.cta", blocks: ["ctaBand", "heroCta"], fallback: "auto",
    filled: (f) => !!(f.heroCtaLabel || f.ctaTitle || f.ctaText) },
  { id: "sc-stats", key: "stats", title: "content.stats", blocks: ["trustStats", "hero", "coachBio"], fallback: "school", rows: "stats" },
  { id: "sc-subject", key: "subject", title: "content.subject", blocks: ["subjectFeature"], fallback: "auto",
    filled: (f) => !!(f.subjectEyebrow || f.subjectTitle || f.subjectText || f.subjectItems.length) },
  { id: "sc-steps", key: "steps", title: "content.steps", blocks: ["steps"], fallback: "auto", rows: "steps" },
  { id: "sc-features", key: "features", title: "content.features", blocks: ["featureGrid"], rows: "features" },
  { id: "sc-quotes", key: "testimonials", title: "content.testimonials", blocks: ["testimonials"], rows: "testimonials" },
  { id: "sc-faq", key: "faq", title: "content.faq", blocks: ["faq"], fallback: "school", rows: "faq" },
  { id: "sc-honor", key: "honor", title: "content.honor", blocks: ["honorBoard"], rows: "honor" },
  { id: "sc-apps", key: "apps", title: "content.apps", blocks: ["appBadges", "ctaBand"], filled: (f) => !!(f.apps.ios || f.apps.android) },
];

const ARABIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";
const arabicNumber = (n) => String(n).replace(/\d/g, (d) => ARABIC_DIGITS[d]);

/** "محتوى الصفحة الرئيسية": the texts of the blocks the site's theme puts on its home page. */
export default function SiteContent() {
  const toast = useRef(null);
  const [form, setForm] = useState(null);
  const [branding, setBranding] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState("");
  const [site, setSite] = useState(null);

  useEffect(() => {
    document.title = properties.site_name + " | " + t("content");
    apiFetch("/teachers/me")
      .then((me) => {
        setBranding(me.branding || {});
        const read = readContent(me.branding && me.branding.homeJson);
        setForm(read);
        setSaved(writeContent(read));
        // a link to one section (/settings/home-content#sc-faq) lands on it once the form is drawn
        const target = window.location.hash.slice(1);
        if (target) setTimeout(() => { const el = document.getElementById(target); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }, 350);
        platformInfo().then((info) => setSite(siteOf(me, info))).catch(() => setSite(siteOf(me, null)));
      })
      .catch(() => {
        setBranding({});
        setForm(emptyContent());
      });
  }, []);

  const say = (severity, detail) => toast.current && toast.current.show({ severity, summary: severity === "error" ? t("common.alert") : t("common.done"), detail });

  const save = () => {
    const body = {};
    KEEP.forEach((k) => {
      body[k] = branding[k] || "";
    });
    body.homeJson = writeContent(form);
    setSaving(true);
    apiFetch("/teachers/me/branding", { method: "PUT", body })
      .then((me) => {
        setBranding(me.branding || branding);
        setSaved(body.homeJson);
        say("success", t("content.saved"));
      })
      .catch((e) => say("error", (e && e.message) || t("common.failed")))
      .finally(() => setSaving(false));
  };

  const text = (key, label, max, area) => (
    <Box label={label} value={form[key]} max={max} area={area} wide onChange={(v) => setForm({ ...form, [key]: v })} />
  );

  const theme = branding ? themeOf(branding) : null;
  const school = !!(theme && shows(theme, "hero"));
  const dirty = !!form && writeContent(form) !== saved;

  // each section with what this theme does with it: shown, shown with its own words until filled, or not shown
  const stateOf = (s) => {
    if (!theme || !shows(theme, ...s.blocks)) return "off";
    const filled = s.filled ? s.filled(form) : form[s.rows].length > 0;
    if (filled) return "on";
    return s.fallback === "auto" || (s.fallback === "school" && school) ? "auto" : "hidden";
  };
  const rank = (s) => {
    if (!theme || !theme.home) return 0;
    const at = s.blocks.map((b) => theme.home.indexOf(b)).filter((i) => i >= 0);
    return at.length ? Math.min(...at) : 999;
  };
  const list = form
    ? SECTIONS.map((s) => ({ ...s, state: stateOf(s) }))
      .sort((a, b) => (a.state === "off") - (b.state === "off") || rank(a) - rank(b))
      .map((s, i) => ({ ...s, n: arabicNumber(i + 1) }))
    : [];
  const firstOff = list.findIndex((s) => s.state === "off");
  const titleOf = (s) => (s.key === "subject" && theme && theme.subject ? `${t(s.title)} — ${SUBJECT_NAMES[theme.subject] || ""}` : t(s.title));
  const whereOf = (s) => {
    const names = [];
    s.blocks.forEach((b) => {
      if (shows(theme, b) && names.indexOf(BLOCK_NAMES[b]) < 0) names.push(BLOCK_NAMES[b]);
    });
    return names.join(" · ");
  };

  const bodyOf = (s) => {
    switch (s.key) {
      case "cta":
        return (
          <div className="ui-fields">
            {text("heroCtaLabel", t("content.ctaLabel"), 60)}
            {text("ctaTitle", t("content.ctaTitle"), 120)}
            {text("ctaText", t("content.ctaText"), 300, true)}
          </div>
        );
      case "stats":
        return <Rows name="stats" form={form} setForm={setForm} fields={[["value", t("content.statValue"), { max: 30 }], ["label", t("content.statLabel"), { max: 60 }]]} />;
      case "subject":
        return (
          <>
            <div className="ui-fields">
              {text("subjectEyebrow", t("content.subjectEyebrow"), 40)}
              {text("subjectTitle", t("content.subjectTitle"), 120)}
              {text("subjectText", t("content.subjectText"), 400, true)}
            </div>
            <h4 className="ui-sub">{t("content.subjectItemsHint")}</h4>
            <Rows name="subjectItems" form={form} setForm={setForm}
              fields={[["title", t("content.title"), { max: 80 }], ["sub", t("content.subjectSub"), { max: 120, wide: true }]]} />
          </>
        );
      case "steps":
        return <Rows name="steps" form={form} setForm={setForm} fields={[["title", t("content.title"), { max: 80 }], ["text", t("content.text"), { max: 200, area: true, wide: true }]]} />;
      case "features":
        return (
          <Rows name="features" form={form} setForm={setForm}
            fields={[["icon", t("content.icon"), { max: 8 }], ["title", t("content.title"), { max: 80 }], ["text", t("content.text"), { max: 300, area: true, wide: true }]]} />
        );
      case "testimonials":
        return (
          <Rows name="testimonials" form={form} setForm={setForm}
            fields={[["name", t("content.name"), { max: 80 }], ["role", t("content.role"), { max: 80 }], ["text", t("content.quote"), { max: 400, area: true, wide: true }]]} />
        );
      case "faq":
        return <Rows name="faq" form={form} setForm={setForm} fields={[["q", t("content.question"), { max: 200, wide: true }], ["a", t("content.answer"), { max: 600, area: true, wide: true }]]} />;
      case "honor":
        return <Rows name="honor" form={form} setForm={setForm} fields={[["name", t("content.name"), { max: 60 }], ["score", t("content.score"), { max: 20 }]]} />;
      case "apps":
        return (
          <div className="ui-fields">
            <Box label={t("content.ios")} value={form.apps.ios} max={500} dir="ltr" placeholder="https://apps.apple.com/..." wide
              onChange={(v) => setForm({ ...form, apps: { ...form.apps, ios: v } })} />
            <Box label={t("content.android")} value={form.apps.android} max={500} dir="ltr" placeholder="https://play.google.com/..." wide
              onChange={(v) => setForm({ ...form, apps: { ...form.apps, android: v } })} />
          </div>
        );
      default:
        return null;
    }
  };

  // what each section says about itself, under its title
  const HINTS = { stats: "content.statsHint", steps: "content.stepsHint", honor: "content.honorHint", subject: "content.subjectHint" };

  return (
    <>
      <Header
        pageName={t("content")}
        action={{
          label: saving ? "جاري الحفظ…" : t("common.save"),
          icon: saving ? "pi pi-spin pi-spinner" : "pi pi-check",
          disabled: saving || !dirty,
          onClick: save,
        }}
      />
      <Toast ref={toast} />
      <div className="content">
        <div className="container se-page">
          {form ? (
            <>
              <ThemeBar theme={theme} site={site} here="content"
                editable={["trustStats", "subjectFeature", "steps", "featureGrid", "testimonials", "faq", "honorBoard", "appBadges", "ctaBand", "heroCta"]} />

              <div className="se-grid">

                <div className="se-main">
                  {theme && !theme.home ? (
                    <Alert kind="warn" title="الأقسام دي مش هتظهر بالثيم الحالي">
                      ثيم «{theme.name}» كلاسيكي وصفحته الرئيسية ثابتة. اكتب المحتوى براحتك، وهيظهر أول ما تختار ثيم من الثيمات الجديدة
                      من <Link to="/settings/look">شكل الموقع</Link>.
                    </Alert>
                  ) : (
                    <Alert kind="info" title={t("content.noteTitle")}>{t("content.note")}</Alert>
                  )}

                  {list.map((s, i) => (
                    <React.Fragment key={s.id}>
                      {i === firstOff && theme && theme.home ? <div className="se-group">مش ظاهرة في ثيمك الحالي — تقدر تكتبها لثيم تاني</div> : null}
                      <section className={`ui-sec ${s.state === "off" ? "is-off" : ""}`} id={s.id} style={{ "--d": `${Math.min(i, 6) * 60}ms` }}>
                        <h3>
                          <span className="n">{s.n}</span>
                          <span className="t">
                            {titleOf(s)}
                            {HINTS[s.key] ? <small>{t(HINTS[s.key])}</small> : null}
                          </span>
                          <span className="act"><StateChip state={s.state} /></span>
                        </h3>
                        {theme && theme.home ? (
                          <div className="se-why">
                            <i className={s.state === "off" ? "pi pi-eye-slash" : "pi pi-eye"}></i>
                            <span>
                              {s.state === "off"
                                ? `ثيم «${theme.name}» مش بيعرض القسم ده.`
                                : `بيظهر في صفحتك في: ${whereOf(s)}.`}
                              {s.state === "auto" ? " لحد ما تكتبه، الموقع بيعرض كلام جاهز مناسب للثيم." : ""}
                              {s.state === "hidden" ? " لو سيبته فاضي القسم مش هيظهر." : ""}
                            </span>
                          </div>
                        ) : null}
                        {bodyOf(s)}
                      </section>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="card">
              <Card>
                <div className="ui-form-wait" role="status">
                  <span className="spin" aria-hidden="true"></span>
                  <b>جاري تحميل المحتوى…</b>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
